import "./modules/productGalleryModal.js";
import "./modules/interestList.js";
import { showCartSuccessModal } from "./modules/cartSuccessModal.js";
import { openProductGalleryAt } from "./modules/productGalleryModal.js";
import { fetchJson } from "./lib/fetchJson.js";
import { preloadImages } from "./lib/utils.js";
import { showCartErrorToast } from "./lib/toast.js";

class FFProductGallery extends HTMLElement {
	viewerIndex = '0'
  DOM: {
    galleryTriggers: NodeListOf<HTMLElement>,
    thumbs: NodeListOf<HTMLElement>,
    viewer: HTMLElement | null,
		videoViewer: HTMLElement | null,
    videoViewerImg: HTMLImageElement | null,
    viewerImg: HTMLImageElement | null,
  }

	constructor(){
    super();
		this.DOM = {
			galleryTriggers: this.querySelectorAll('.js-thumb, .js-viewer'),
			thumbs: this.querySelectorAll('.js-thumb'),
			viewer: this.querySelector('.js-viewer'),
			videoViewer: this.querySelector(".js-videoViewer"),
			videoViewerImg: this.querySelector(".js-videoViewer img"),
			viewerImg: this.querySelector('.js-viewer img'),
		}
		this.events();
		this.preloadGalleryImages();
	}

	preloadGalleryImages(){
		const images = [...this.DOM.thumbs].reduce((acc, thumb) => {
			const url = thumb.getAttribute('data-image');
			if( !url ) return acc;
			return [...acc, url];
		}, [] as string[]);

		if( images.length === 0 ) return;

		const runPreload = () => preloadImages(images);
		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(runPreload, { timeout: 2000 });
		} else {
			window.addEventListener('load', runPreload, { once: true });
		}
	}

	events(){
    this.DOM.galleryTriggers.forEach(el => {
      el.addEventListener('click', this.openClick.bind(this));
    })
    this.DOM.thumbs.forEach(el => {
      el.addEventListener('mouseenter', this.thumbHover.bind(this));
    })
	}

	openClick(e: MouseEvent){
		e.preventDefault();
    const target = e.currentTarget
		if( target instanceof HTMLAnchorElement ) return //Videos
    if( !(target instanceof HTMLElement) ) return
		const slide = parseInt(target.getAttribute('data-slide') || "1");
		openProductGalleryAt(slide - 1);
	}

	thumbHover(e: MouseEvent){
		const target = e.currentTarget;
    if( !(target instanceof HTMLElement) ) return
		const src = target.getAttribute('data-image');
		const slide = target.getAttribute('data-slide');

		if( !(this.DOM.viewer instanceof HTMLElement )) return
		if( this.viewerIndex === slide ) return;

    this.DOM.thumbs.forEach( el => el.classList.remove("active") )
		target.classList.add('active');

		const video = target.getAttribute('href');
		if( video && this.DOM.videoViewer instanceof HTMLElement ){
			this.DOM.viewer.style.display = "none"
			this.DOM.videoViewer.style.display = "block"
			this.DOM.videoViewer.setAttribute('href', video);
			if( this.DOM.videoViewerImg instanceof HTMLImageElement ){
				this.DOM.videoViewerImg.src = src || "";
			}
		}else{
			this.DOM.viewer.style.display = "block"
			this.DOM.viewer.setAttribute('data-slide', slide || "");
			if( this.DOM.videoViewer ){
				this.DOM.videoViewer.style.display = "none"
			}
			if( this.DOM.viewerImg instanceof HTMLImageElement ){
				this.DOM.viewerImg.removeAttribute('srcset');
				this.DOM.viewerImg.removeAttribute('sizes');
				this.DOM.viewerImg.src = src || "";
			}
		}
		this.viewerIndex = slide || "0";
	}
};
customElements.define('ff-product-gallery', FFProductGallery);

//Eg. https://store.freeflysystems.com/products/alta-x-vibration-isolator-cartridges
interface PurchaseListEntry {
  variantId: string
  el: HTMLElement
  qtyInput: HTMLInputElement
}
class FFPurchaseList extends HTMLElement {

	DOM: {
    addBtn: HTMLElement | null
    entries: PurchaseListEntry[]
  }

	constructor(){
    super();
		this.DOM = {
			addBtn: this.querySelector('.js-addPurchaseList'),
			entries: [...this.querySelectorAll('.js-entry')].reduce( (acc, cur) => {
        if( !(cur instanceof HTMLElement) ) return acc
        const variantId = cur.getAttribute('data-value');
        if( !variantId ) return acc
        const qtyInput = cur.querySelector(".qty")
        if( !(qtyInput instanceof HTMLInputElement) ) return acc
        return [
          ...acc,
          {
            variantId,
            el: cur,
            qtyInput,
          }
        ]
      }, [] as PurchaseListEntry[]),
		}
		this.events();
	}

	events(){
		this.DOM.addBtn?.addEventListener('click', this.addClick.bind(this) );
	}

	getRequestedVariants(){
		const requestedVariants = this.DOM.entries.reduce((acc, cur) => {
      const qty = parseInt(cur.qtyInput.value)
			if( qty < 1 ) return acc
      return {
        ...acc,
        [cur.variantId]: qty
      }
    }, {} as { [key: string]: number })
		return requestedVariants;
	}

	addClick(){
		const requestedVariants = this.getRequestedVariants();
		if( Object.keys(requestedVariants).length === 0 ) return;
		const data = {
			updates: requestedVariants
		}
		this.sendUpdateRequest(data)
			.then( () => {
				this.emptyQuantities();
				const handle = document.body.getAttribute('data-handle');
        if( !handle ) return
				showCartSuccessModal(handle);
			})
			.catch(() => {
				showCartErrorToast()
			});
	}

	emptyQuantities(){
    this.DOM.entries.forEach((el) => {
      el.qtyInput.value = "0"
    })
	}

	sendUpdateRequest( data: { updates: { [key: string]: number }} ){
    return fetchJson("/cart/update.js", {
			method: "POST",
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', },
			body: JSON.stringify(data)
		})
	}
}
customElements.define('ff-purchase-list', FFPurchaseList);

interface ProductTab {
  button: HTMLElement
  panel: HTMLElement
}
class FFProductTabs extends HTMLElement {
  tabs: ProductTab[]

	constructor(){
    super();
    this.tabs = [...this.querySelectorAll('.js-tab')].reduce((acc, cur) => {
      if( !(cur instanceof HTMLElement) ) return acc
      const slug = cur.getAttribute('data-slug');
      if( !slug ) return acc
      const panel = this.querySelector('.js-tabPanel[data-slug="' + slug + '"]')
      if( !(panel instanceof HTMLElement) ) return acc
      return [
        ...acc,
        {
          button: cur,
          panel
        }
      ]
    }, [] as ProductTab[])
		this.events();
	}

	events(){
    this.tabs.forEach(el => {
      el.button.addEventListener('click', this.tabClick.bind(this))
    })
	}

	closeAll(){
    this.tabs.forEach(el => {
      el.button.classList.remove('active')
      el.panel.classList.remove('active')
    })
	}

	tabClick(e: MouseEvent){
		e.preventDefault();
		this.closeAll();
    const data = this.tabs.find( el => el.button === e.currentTarget )
    if( !data ) return
		data.button.classList.add('active');
    data.panel.classList.add('active');
	}
};
customElements.define('ff-product-tabs', FFProductTabs);